import {
  actionColumn,
  Chip,
  column,
  Css,
  dateColumn,
  emptyCell,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridSortConfig,
  GridTableLayout,
  Icon,
  multiFilter,
  Palette,
  RowStyles,
  simpleHeader,
  SimpleHeaderAndData,
  TagType,
  toggleFilter,
  Tooltip,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { dateCell, emptyCellDash, tagCell } from "src/components";
import {
  LotType,
  Order,
  ProjectFilter,
  ProjectOrder,
  ProjectStatus,
  ProjectTypeFilter,
} from "src/generated/graphqlTypes";
import {
  ProjectsPageInternalUserFragment,
  ProjectsPageMetadataQuery,
  ProjectsPageProject_Community_Fragment,
  ProjectsPageProject_Structure_Fragment,
  ProjectsPageProjectFragment,
  ProjectsPageProjectsQuery,
  useProjectsPageMetadataQuery,
  useProjectsPageProjectsQuery,
} from "src/routes/projects/ProjectsPage.generated";
import { createProjectDashboardUrl } from "src/RouteUrls";
import {
  formatList,
  maybeDeTagId,
  pluralize,
  projectStatusDetails,
  queryResult,
  safeEntries,
  sortBy,
  stageCodeToNameMapper,
} from "src/utils";
import { openNewTab } from "src/utils/window";
import { parseOrder, toOrder } from "../../utils/ordering";

export function ProjectsPage() {
  const currentInternalUserQuery = useProjectsPageMetadataQuery({ fetchPolicy: "cache-first" });
  return queryResult(currentInternalUserQuery, {
    data: ({ currentInternalUser, internalUsers, markets, cohorts, developments, communities }) => {
      return (
        <ProjectsDataView
          currentUser={currentInternalUser!}
          internalUsers={internalUsers}
          markets={markets}
          cohorts={cohorts}
          developments={developments}
          communities={communities}
        />
      );
    },
  });
}

type ProjectsDataViewProps = {
  currentUser: ProjectsPageInternalUserFragment;
  internalUsers: ProjectsPageMetadataQuery["internalUsers"];
  markets: ProjectsPageMetadataQuery["markets"];
  cohorts: ProjectsPageMetadataQuery["cohorts"];
  developments: ProjectsPageMetadataQuery["developments"];
  communities: ProjectsPageMetadataQuery["communities"];
};

export function ProjectsDataView({
  currentUser,
  markets,
  internalUsers,
  cohorts,
  developments,
  communities,
}: ProjectsDataViewProps) {
  const [orderBy, setOrderBy] = useState<ProjectOrder>({ name: Order.Asc });
  const filterDefs: FilterDefs<ProjectFilter> = useMemo(
    () => {
      const teamMembers = multiFilter({
        options: internalUsers ?? [],
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
        defaultValue: [currentUser.id],
      });

      const latestActiveStage = multiFilter({
        options: safeEntries(stageCodeToNameMapper).map(([code, name]) => ({ id: code, name })),
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      });

      const market = multiFilter({ options: markets, getOptionValue: (o) => o.id, getOptionLabel: (o) => o.name });

      const status = multiFilter({
        options: safeEntries(projectStatusDetails).map(([code, name]) => ({ id: code, name })),
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
        defaultValue: [ProjectStatus.Active],
      });

      const type = multiFilter({
        options: ProjectTypeFilter.toValues().map((v) => ({
          id: v,
          name: v === ProjectTypeFilter.StandaloneProject ? "Standalone" : v,
        })),
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
        defaultValue: [ProjectTypeFilter.StandaloneProject, ProjectTypeFilter.Structure, ProjectTypeFilter.Unit],
      });

      const parentRecursive = multiFilter({
        label: "Community",
        options: communities.sortBy((c) => c.name),
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      });

      const development = multiFilter({
        options: developments,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      });

      const cohort = multiFilter({
        options: sortBy(cohorts, ({ name }) => name),
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      });

      const lotType = multiFilter({
        options: [LotType.Bool, LotType.Boyl, LotType.Hbl, LotType.Btr, LotType.Fee].map((v) => ({ id: v, name: v })),
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      });

      const showInCheckout = toggleFilter({ label: "Shown in checkout" });
      const isTest = toggleFilter({ label: "Only test projects" });

      return {
        teamMembers,
        latestActiveStage,
        market,
        status,
        type,
        parentRecursive,
        development,
        cohort,
        lotType,
        showInCheckout,
        isTest,
      };
    },
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser, markets, internalUsers],
  );

  const layoutState = useGridTableLayoutState<ProjectFilter>({
    persistedFilter: { filterDefs, storageKey: "projectFilter" },
    search: "server",
  });

  const query = useProjectsPageProjectsQuery({
    variables: {
      filter: { ...layoutState.filter, search: layoutState.searchString ?? "" },
      orderBy,
      page: { offset: 0, limit: PAGE_SIZE },
    },
  });

  const sorting: GridSortConfig = useMemo(
    () => ({
      on: "server",
      onSort: (key, direction) => setOrderBy(toOrder(key, direction)),
      value: parseOrder(orderBy),
    }),
    [orderBy],
  );

  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.projectsPage.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data.projectsPage.entities?.length ?? 0, limit: PAGE_SIZE } },
      });
    }
  }, [query]);

  return (
    <GridTableLayout
      layoutState={layoutState}
      tableProps={{
        query,
        createRows,
        columns,
        sorting,
        rowStyles,
        as: "virtual",
        emptyFallback: "No projects found that match the given filters.",
        infiniteScroll: { onEndReached: maybeFetchNextPage },
      }}
    />
  );
}

type Row = SimpleHeaderAndData<ProjectsPageProjectFragment>;

const columns: GridColumn<Row>[] = [
  column<Row>({
    id: "address",
    name: "Name",
    header: "Name",
    data: (data) => data.name,
    w: 3,
    mw: "250px",
    serverSideSortKey: "name",
  }),
  column<Row>({
    id: "hierarchy",
    name: "Project Hierarchy",
    header: "Project Hierarchy",
    data: (data) =>
      data.__typename !== "StandaloneProject" ? (
        <Tooltip title={mapHierarchyInformation(data)}>
          <Chip text={data.__typename} compact />
        </Tooltip>
      ) : (
        emptyCellDash
      ),
    w: "160px",
  }),
  column<Row>({
    id: "homeowner",
    name: "Homeowner",
    header: "Homeowner",
    data: ({ primaryHomeowner }) => {
      return primaryHomeowner ? primaryHomeowner.shortName : "";
    },
    w: "188px",
    serverSideSortKey: "homeowner",
  }),
  column<Row>({
    id: "status",
    name: "Status",
    header: "Status",
    data: (data) => tagCell(projectStatusToTagType[data.status.code], projectStatusDetails[data.status.code]),
    w: "160px",
    serverSideSortKey: "status",
  }),
  column<Row>({
    id: "latestActiveStage",
    name: "Latest Stage",
    header: "Latest Stage",
    data: (data) => data.latestActiveStage?.stage.name ?? "-",
    w: "140px",
    serverSideSortKey: "latestActiveStage",
  }),
  column<Row>({
    id: "market",
    name: "Market",
    header: "Market",
    data: (data) => data.market?.name,
    w: "140px",
    serverSideSortKey: "market",
  }),
  column<Row>({
    id: "community",
    name: "Community",
    header: "Community",
    data: (data) => data.cohort?.name || data.parentsRecursive.find((p) => p.__typename === "Community")?.name,
    w: "220px",
    serverSideSortKey: "cohort",
  }),
  dateColumn<Row>({
    id: "targetEndDate",
    name: "Target End Date",
    header: "Target End Date",
    data: (data) => {
      return dateCell(data.targetEndDate);
    },
    w: "160px",
    serverSideSortKey: "targetEndDate",
  }),
  column<Row>({
    id: "projectLead",
    name: "Project Lead",
    header: "Project Lead",
    data: (data) => data.projectLead?.user?.name ?? "",
    w: "140px",
    serverSideSortKey: "projectLead",
  }),
  column<Row>({
    id: "inCheckout",
    name: "In Checkout",
    header: "In Checkout",
    data: (data) =>
      isCommunityOrStructure(data) && data.showInCheckout ? <Icon icon="check" color={Palette.Gray700} /> : emptyCell,
    w: "140px",
  }),
  column<Row>({
    id: "ID",
    name: "ID",
    header: "ID",
    data: (data) => maybeDeTagId(data.id),
    w: "80px",
    serverSideSortKey: "id",
  }),
  actionColumn<Row>({
    id: "hop",
    name: "HOP",
    header: "HOP",
    data: (data) => (
      <div
        onClick={(e) => {
          e.preventDefault();
          openNewTab(data.hopUrl);
        }}
        css={Css.cursorPointer.$}
        data-testid="hopLink"
      >
        <Icon icon="linkExternal" color={Palette.Gray700} inc={2} />
      </div>
    ),
    w: "60px",
  }),
];

function createRows(data: ProjectsPageProjectsQuery | undefined): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...(data?.projectsPage.entities?.map((p) => ({ kind: "data" as const, id: p.id, data: p })) || []),
  ];
}

const rowStyles: RowStyles<Row> = { header: {}, data: { rowLink: ({ id }) => createProjectDashboardUrl(id) } };

const projectStatusToTagType: Record<ProjectStatus, TagType> = {
  [ProjectStatus.Active]: "info",
  [ProjectStatus.Hold]: "update",
  [ProjectStatus.Disqualified]: "neutral",
  [ProjectStatus.Completed]: "success",
  [ProjectStatus.UnderWarranty]: "success",
};

export function mapHierarchyInformation(data: ProjectsPageProjectFragment): string {
  // Group all related projects by hierarchy level
  const parents = [...data.parentsRecursive].reverse();
  const groupedProjects = [...parents, ...data.childrenRecursive].groupBy((p) => p.__typename ?? "");
  // Return relevant information for each level
  return groupedProjects
    .toEntries()
    .map(([level, projects]) => {
      // Show the number of projects if there are too many to list individually
      return projects.length > 3
        ? `${projects.length} ${pluralize(projects.length, level)}`
        : `${level}: ${formatList(projects.map((p) => p.name))}`;
    })
    .join(", ");
}

function isCommunityOrStructure(
  data: ProjectsPageProjectFragment,
): data is ProjectsPageProject_Community_Fragment | ProjectsPageProject_Structure_Fragment {
  return data.__typename === "Community" || data.__typename === "Structure";
}

const PAGE_SIZE = 100;
